// very POC, much deprecated code below!! (createScriptProcessor is deprecated since 2014, but there is no replacement in Safari)

import { useEffect, useRef, useState } from "react";
import { useMediaDevices } from "@dvrt/web-phone-client";

export type VolumeCallback = (vol: number) => void;

export class VolumeMeter {
    private readonly analyser: AnalyserNode;
    private readonly onVolume: VolumeCallback;
    private readonly processor: ScriptProcessorNode;
    private readonly stream: MediaStream;

    constructor(stream: MediaStream, onVolume?: VolumeCallback) {
        this.stream = stream;
        this.onVolume = onVolume;
        const audioContext = new AudioContext();
        this.analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);
        this.processor = audioContext.createScriptProcessor(2048, 1, 1);

        this.analyser.smoothingTimeConstant = 0.8;
        this.analyser.fftSize = 1024;

        microphone.connect(this.analyser);
        this.analyser.connect(this.processor);
        this.processor.connect(audioContext.destination);

        this.onaudioprocess = this.onaudioprocess.bind(this);

        this.processor.addEventListener("audioprocess", () => this.onaudioprocess());
    }

    destroy() {
        this.processor.disconnect();
        this.processor.removeEventListener("audioprocess", () => this.onaudioprocess());
        for (const track of this.stream.getTracks()) track.stop();
    }

    private onaudioprocess() {
        const array = new Uint8Array(this.analyser.frequencyBinCount);
        this.analyser.getByteFrequencyData(array);
        let values = 0;

        const length = array.length;
        for (let i = 0; i < length; i++) {
            values += array[i];
        }

        this.onVolume(Math.round(cubicEaseOut(values / length / 100) * 100));
    }
}

function cubicEaseOut(p: number): number {
    const f = p - 1;
    return f * f * f + 1;
}

export async function createMeter(deviceId: string, onVolume: VolumeCallback) {
    console.debug("Create VolumeMeter");
    const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
    });
    return new VolumeMeter(stream, onVolume);
}

export const useVolumeMeter = (deviceId: string | null, onVolume: VolumeCallback): VolumeMeter => {
    const meterInstance = useRef<VolumeMeter>(null);
    const mediaDevices = useMediaDevices();

    const isDeviceValid = mediaDevices.devices.audioinput.filter((d) => d.deviceId === deviceId).length > 0;

    useEffect(() => {
        if (meterInstance.current) meterInstance.current.destroy();

        if (deviceId && isDeviceValid)
            createMeter(deviceId, onVolume).then((meter) => {
                meterInstance.current = meter;
            });

        return () => {
            if (meterInstance.current) meterInstance.current.destroy();
        };
    }, [deviceId, isDeviceValid]);

    return meterInstance.current;
};

export const useVolumeLevel = (deviceId: string, enabled: boolean) => {
    const [volume, setVolume] = useState<number>(0);

    useVolumeMeter(enabled ? deviceId : null, (vol) => {
        if (vol !== volume) setVolume(vol);
    });

    return { volume: enabled ? volume : 0 };
};
